// eslint-disable-next-line
import { action, makeAutoObservable, runInAction } from 'mobx';
import { IIntegration, IIntegrationCategoryPreset, IIntegrationPreset, INTEGRATION_CATEGORY, INTEGRATION_CODE, ISelectedIntegration, IShippingConnection, SHIPPING_INTEGRATION_CATEGORY, SHIPPING_CONNECTION_CODE } from '../../../../models/IntegrationsTypes';
import IntegrationsService from '../../../../services/api/integrations-service';
import ShippingIntegrationsService from '../../../../services/api/shipping-integrarions-service';

import { rootContainer, TYPE } from '../../../../services/container/root';
import IntegrationStore, { IIntegrationStore } from './integration-store';
import ShippingIntegrationStore from './shipping-integration-store';
import AggregatorsInfoStore from '../../../return-methods/stores/aggregators-info-store';

export interface IIntegrationListStore {
    integrations: IIntegrationStore[];
    integrationPresets: IIntegrationCategoryPreset[];
    load(): void;
    get isLoading(): boolean;
}

class IntegrationsListStore {
    integrations: IIntegrationStore[] = [];
    integrationPresets: IIntegrationCategoryPreset[] = [];
    private _isLoading: boolean = false;
    private _aggregatorsInfoStore?: AggregatorsInfoStore;

    public get isLoading(): boolean {
        return this._isLoading;
    }

	private get IntegrationsService(): IntegrationsService {
		return rootContainer.get<IntegrationsService>(TYPE.IntegrationsService);
	}

    private get shippingIntegrationsService(): ShippingIntegrationsService {
        return rootContainer.get<ShippingIntegrationsService>(TYPE.ShippingIntegrationsService);
    }

    public get aggregatorsStore(): AggregatorsInfoStore | undefined { return this._aggregatorsInfoStore; }

	constructor() {
		makeAutoObservable(this);
	}
    
    async load() {
        this._isLoading = true;
        const [, integrations, shippingIntegrationsResponse] = await Promise.all([
            this.loadPresets(),
            this.IntegrationsService.getIntegrations(),
            this.shippingIntegrationsService.getIntegrations(),
        ]);
        const shippingIntegrations = shippingIntegrationsResponse.list;
        runInAction(() => {
            this.setupIntegrations(integrations, shippingIntegrations);
            this._aggregatorsInfoStore = new AggregatorsInfoStore(shippingIntegrationsResponse.settings.aggregators_info);
            this._isLoading = false;
        });
    }

    @action.bound
    private setupIntegrations(integrations: IIntegration[], shippingIntegrations: IShippingConnection[]): void {
        /** special case - leonisa etc, we might have special integrations that are per single store only and should not have ui representation 
         * filtering out integrations that are not defined in presets */
        const filterUnkownIntegrations = (integrationList: (IIntegration | IShippingConnection)[]) => {
            return integrationList.filter(int => {
                const serviceType = int.hasOwnProperty('service_type') ? (int as IIntegration).service_type : SHIPPING_INTEGRATION_CATEGORY.SHIPPING;
                const presetCategory = this.integrationPresets.find(p => p.id === serviceType);
                if (presetCategory) {
                    const serviceCode = presetCategory.id === SHIPPING_INTEGRATION_CATEGORY.SHIPPING
                        ? (int as IShippingConnection).aggregator_code
                        : (int as IIntegration).service_code;
                    const presetIntegration = presetCategory.integrations.find(p => p.id === serviceCode);
                    if (presetIntegration) {
                        return true;
                    }
                }
                return false;
            });
        };

        const mapIntegrationsToStores = (integrationList: (IIntegration | IShippingConnection)[]) => {
            return integrationList.map(integration => {
                if (!integration.hasOwnProperty('service_type')) {
                    const presetValue = this.findIntegrationPreset(SHIPPING_INTEGRATION_CATEGORY.SHIPPING, (integration as IShippingConnection).aggregator_code);
                    return new ShippingIntegrationStore(presetValue, integration as IShippingConnection);
                } else {
                    const presetValue = this.findIntegrationPreset((integration as IIntegration).service_type, (integration as IIntegration).service_code);
                    return new IntegrationStore(presetValue, integration as IIntegration);
                }
            });
        }

        this.integrations = mapIntegrationsToStores(filterUnkownIntegrations([...integrations, ...shippingIntegrations]))
    }

    private async loadPresets() {
        const integrationPresets = await this.IntegrationsService.getIntegrationsPresets();
        runInAction(() => {
            this.integrationPresets = integrationPresets.map((integrationPreset: IIntegrationCategoryPreset) => ({
                id: integrationPreset.id,
                label: integrationPreset.label,
                is_allowed: integrationPreset.is_allowed,
                integrations: integrationPreset.integrations.sort((a, b) => a.label.localeCompare(b.label)),
            })).sort((a, b) => a.label.localeCompare(b.label));
		});
    }

    public findIntegrationPreset(serviceType: INTEGRATION_CATEGORY | SHIPPING_INTEGRATION_CATEGORY, serviceCode: INTEGRATION_CODE | SHIPPING_CONNECTION_CODE) {
        let presetValue = {} as IIntegrationPreset;

        this.integrationPresets.forEach(presetCategory => {
            if (presetCategory.id === serviceType) {
                const foundIntegration = presetCategory.integrations.find(integration => integration.id === serviceCode);
                if (foundIntegration) {
                    presetValue = { 
                        ...foundIntegration, 
                        is_allowed: foundIntegration.exclude_from_feature_category || presetCategory.is_allowed 
                    };
                }
            }
        });
        
        return presetValue;
    }

    public addIntegration(selectedIntegration: ISelectedIntegration) {
        const presetValue = this.findIntegrationPreset(selectedIntegration.category, selectedIntegration.value);
        let store = null;
        if (selectedIntegration.category === SHIPPING_INTEGRATION_CATEGORY.SHIPPING ||
            selectedIntegration.category === SHIPPING_INTEGRATION_CATEGORY.DROP_OFF) {
            store = new ShippingIntegrationStore(presetValue);
            store.setChanges({
                aggregator_code: selectedIntegration.value as SHIPPING_CONNECTION_CODE,
            });
        } else {
            store = new IntegrationStore(presetValue);
            store.setChanges({
                service_code: selectedIntegration.value as INTEGRATION_CODE,
                service_type: selectedIntegration.category,
                is_enabled: true,
            });
        }

        this.integrations.push(store);
    }

    public async removeIntegration(integration: IIntegrationStore) {
        this._isLoading = true;
        try {
            await integration.forget();
            runInAction(() => {
                this.integrations = this.integrations.filter(value => value !== integration);
                this._isLoading = false;
            });
        } catch(e) {
            runInAction(() => {
                this._isLoading = false;
            });
        }
    }

    public getIntegrationByCode(code: string): IIntegrationStore|undefined {
        return this.integrations.find(intStore => intStore.serviceCode === code);
    }
}

export default new IntegrationsListStore();
